import { useState, useCallback, useRef, type ReactNode } from "react";
import { cn } from "@/lib/utils/cn";

export interface PullToRefreshBarProps {
  onRefresh: () => Promise<void> | void;
  /** Distance (px) at which the bar triggers release. Defaults to 80. */
  threshold?: number;
  children: ReactNode;
  className?: string;
  /**
   * W35.5 finding P-1 (medium): every list page already passes a
   * `data-testid` (`diaries-pull-to-refresh`, `calendar-pull-to-refresh`,
   * …) but this component accepted no such prop and never spread the rest,
   * so the testid was silently dropped and spec §3.4's "pull-to-refresh on
   * every list view" was unassertable. Forwarded onto the root node.
   */
  "data-testid"?: string;
}

/**
 * PA5 — PullToRefreshBar (mobile).
 *
 * Per `component-api.md §3 PA5`:
 *   - Mobile 列表下拉刷新
 *   - 文案「鬆手刷新」
 *   - Touch-driven; uses pointer events with a touch-action lock.
 */
export function PullToRefreshBar({
  onRefresh,
  threshold = 80,
  children,
  className,
  "data-testid": testId,
}: PullToRefreshBarProps) {
  const [pullDist, setPullDist] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef<number | null>(null);

  const handleRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
      setPullDist(0);
    }
  }, [onRefresh, refreshing]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (window.scrollY > 0) return;
    startYRef.current = e.clientY;
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const startY = startYRef.current;
      if (startY == null || refreshing) return;
      const dy = e.clientY - startY;
      if (dy > 0) {
        // Dampen: only first 1/3 of the pull distance maps to bar height.
        setPullDist(Math.min(threshold * 1.5, dy) / 1.5);
      }
    },
    [refreshing, threshold],
  );

  const onPointerUp = useCallback(() => {
    startYRef.current = null;
    if (pullDist >= threshold && !refreshing) {
      void handleRefresh();
    } else {
      setPullDist(0);
    }
  }, [pullDist, threshold, refreshing, handleRefresh]);

  const released = pullDist >= threshold;

  return (
    <div
      data-component="PullToRefreshBar"
      data-testid={testId}
      data-state={refreshing ? "refreshing" : released ? "released" : "idle"}
      className={cn("relative", className)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{ "--pull-y": `${pullDist}px` } as React.CSSProperties}
    >
      <div
        // Hide the pull hint entirely at rest (`pullDist === 0`) — using
        // `overflow-hidden` + `pointer-events-none` so it never overlays
        // sibling content like the email banner above it.
        className={cn(
          "absolute top-0 inset-x-0 flex items-center justify-center overflow-hidden pointer-events-none",
          "text-meta text-ink-2 select-none",
          "transition-opacity duration-base ease-paper",
          pullDist > 0 ? "opacity-100" : "opacity-0",
        )}
        style={{ height: pullDist }}
        aria-hidden={pullDist === 0}
      >
        {refreshing ? "刷新中…" : released ? "鬆手刷新" : "下拉刷新"}
      </div>
      <div data-pull-content>{children}</div>
    </div>
  );
}
